import { type JSX, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Inbox, Lock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from './Button';
import { Skeleton, TableSkeleton, EmptyState } from './EmptyState';
import type { Failure } from '../../lib/api-state';

/**
 * Standardized fetch-state wrapper. Replace ad-hoc patterns like
 *   {!data && !err && <p>Loading…</p>}
 *   {err && <p className="err">{err}</p>}
 *   {data && data.length === 0 && <p>No rows.</p>}
 * with a single consistent component.
 */
export function FetchState({
  loading,
  error,
  empty,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  emptyIcon: EmptyIcon = Inbox,
  onRetry,
  skeleton = 'rows',
  children,
}: {
  loading?: boolean;
  /**
   * A plain message, or a classified `Failure` (see lib/api-state). A
   * `no_access` failure renders the No Access state with no Retry; other
   * failures render Retry unless `retryable` is false.
   */
  error?: string | Failure | null;
  empty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  emptyIcon?: LucideIcon;
  onRetry?: () => void;
  skeleton?: 'rows' | 'cards' | 'spinner';
  children?: ReactNode;
}): JSX.Element {
  if (loading) {
    if (skeleton === 'spinner') {
      return (
        <div role="status" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48, gap: 8, color: 'var(--text-muted)' }}>
          <RefreshCw
            width={16} height={16}
            style={{ animation: 'spin 900ms linear infinite' }}
          />
          <span style={{ fontSize: '0.875rem' }}>Loading…</span>
        </div>
      );
    }
    if (skeleton === 'cards') {
      return (
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={96} rounded={12} />)}
        </div>
      );
    }
    return <TableSkeleton rows={6} cols={4} />;
  }

  if (error) {
    const failure: Failure | null = typeof error === 'string' ? null : error;
    const noAccess = failure?.kind === 'no_access';
    const message = typeof error === 'string' ? error : error.message;
    const canRetry = !!onRetry && (failure ? failure.retryable : true);
    const tone = noAccess ? 'var(--text-primary)' : 'var(--danger)';
    return (
      <div
        role={noAccess ? 'status' : 'alert'}
        data-state={noAccess ? 'no-access' : 'error'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 32,
          borderRadius: 16,
          background: noAccess ? 'var(--bg-overlay)' : 'var(--danger-soft)',
          border: noAccess ? '1px solid var(--border)' : '1px solid rgba(239,68,68,0.3)',
          textAlign: 'center',
        }}
      >
        {noAccess
          ? <Lock width={28} height={28} color="var(--text-muted)" aria-hidden="true" />
          : <AlertTriangle width={28} height={28} color="var(--danger)" aria-hidden="true" />}
        <div>
          <p style={{ margin: 0, fontWeight: 600, color: tone, fontSize: '0.9rem' }}>
            {noAccess ? 'No access' : "Couldn't load this"}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: 380 }}>
            {message}
          </p>
        </div>
        {canRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw width={14} height={14} />
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (empty) {
    return (
      <EmptyState
        icon={EmptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return <>{children}</>;
}
